/* eslint-disable sonarjs/cognitive-complexity, react/no-unstable-nested-components */
import classNames from 'classnames';
import dynamic from 'next/dynamic';
import PropTypes from 'prop-types';
import React, { useState, useContext } from 'react';
import Dropdown from 'react-bootstrap/Dropdown';
import { useSelector } from 'react-redux';
import { DEAL_CHOOSE_YOUR_OPTIONS_MODAL } from '../../../../brands/default/content/components/deal';
import { BESTSELLER_CUE } from '../../../../brands/default/features';
import { isLightPage } from '../../../../helpers/browser';
import {
  getColumn,
  getImageForVariant,
  getRow,
  getDiscount,
} from '../../../../helpers/chooseYourOptions';
import { formatCurrency } from '../../../../helpers/currency';
import {
  getModalSocialCue,
  useSocialCues,
  isDepositDeal,
} from '../../../../helpers/deals';
import { replaceImageServer } from '../../../../helpers/image';
import { getBackendSocialCueMessage } from '../../../../helpers/socialCues';
import ThemeContext from '../../../../providers/ThemeProvider';
import Message from '../../../_generic/Message';
import PrimaryButton from '../../../_generic/button/PrimaryButton';
import RadioButtonWithLabel from '../../../_generic/radio-button/RadioButtonWithLabel';
import ListMainDeal from '../../ListMainDeal';
import MainDealDiscount from '../../MainDealDiscount';
import MainDealFinePrint from '../../MainDealFinePrint';
import MainDealFullDetails from '../../MainDealFullDetails';
import SocialCueMainDeal from '../../SocialCueMainDeal';
import DealBuyButton from './DealBuyButton';
import DealGiftButton from './DealGiftButton';
/**
 * see https://wowcher.atlassian.net/wiki/spaces/WOWA/pages/2266005505/Choose+Your+Options
 */

export const layoutOptions = {
  travel: 'travel',
};

const ChooseYourQuantity = dynamic(() => import('../ChooseYourQuantity'), {
  ssr: false,
});

const DealChooseYourOptionsModal = ({
  buttonRef = undefined,
  deal = {},
  finePrintCollapsed,
  handleBuyButtonClick,
  initialColumnSel = 0,
  isOptionSelected = () => {},
  isVisible = false,
  layout,
  secondCheckout = false,
  setFinePrintCollapsed,
}) => {
  const [isVipUser] = useSelector((state) => [
    state?.user?.userprofile?.vipUser,
  ]);
  const [rowSelection, setRowSelection] = useState(0); // 1st option
  const [columnSelection, setColumnSelection] = useState(initialColumnSel); // 2nd Option (hidden if only 1 option)
  const [selectedOption, setSelectedOption] = useState(-1);
  const [payDeposit, setPayDeposit] = useState(deal?.depositAvailable || false); // deposit or payment in full
  const [showEmptyMessage, setShowEmptyMessage] = useState(false);
  const theme = useContext(ThemeContext);
  const socialCuesData = useSocialCues(deal.id);
  const row = getRow(deal, isVipUser);
  const [column, setColumn] = useState(() => getColumn(deal, rowSelection));
  const bestsellerProduct = BESTSELLER_CUE
    ? deal?.products?.find((element) => element.bestSellerClaim)
    : {};

  const isLayoutTravel = Boolean(layout === layoutOptions.travel);
  const onRowChange = (rowId) => {
    const id = Number.parseInt(rowId, 10);
    const col = getColumn(deal, id, isVipUser);
    setRowSelection(id);
    setColumn(col);
    const colSelection = col.items.length === 1 ? col.items[0].id : 0;

    setColumnSelection(colSelection);

    const selection = deal.products.findIndex(
      (item) => item.rowId === id && item.columnId === colSelection,
    );
    setSelectedOption(selection);
    setShowEmptyMessage(false);
    if (column.items.length === 1) isOptionSelected(true);
  };

  const onSecondRowChange = (colId) => {
    const secondValue = Number.parseInt(colId, 10);
    setColumnSelection(secondValue);

    if (deal.depositAvailable) {
      setPayDeposit(Boolean(secondValue));
    }
    const selection = deal.products.findIndex(
      (item) => item.rowId === rowSelection && item.columnId === secondValue,
    );
    setSelectedOption(selection);
    setShowEmptyMessage(false);
    isOptionSelected(true);
  };

  const showError = () => {
    setShowEmptyMessage(true);
  };

  const DepositMessage = () => {
    if (!isDepositDeal(deal)) {
      return null;
    }

    return (
      <Message
        extraClasses="message-center message-border-radius green-theme"
        html
        message={DEAL_CHOOSE_YOUR_OPTIONS_MODAL.depositMessage.replace(
          '%price',
          formatCurrency(deal?.currency, deal?.depositPrice),
        )}
        showIcon={false}
      />
    );
  };

  const getGiftButton = (itemSelected) => {
    if (!deal.giftable) return null;
    if (itemSelected < 0)
      return (
        <div className="gift-button">
          <PrimaryButton
            aria-label={DEAL_CHOOSE_YOUR_OPTIONS_MODAL.giveAsAGiftButton}
            className="full-width outline margin"
            onClick={() => showError()}
          >
            {DEAL_CHOOSE_YOUR_OPTIONS_MODAL.giveAsAGiftButton}
          </PrimaryButton>
        </div>
      );

    return (
      <div className="dealgift-button">
        <DealGiftButton
          className="full-width outline margin"
          deal={deal}
          position={selectedOption}
        />
      </div>
    );
  };

  const imageVariant = getImageForVariant(deal, rowSelection, columnSelection);
  const selectBox = ({ data, value, action, htmlId }) => {
    data.items.sort((a, b) => a.id - b.id);
    const selectedItem = value ? data.items.find((x) => x.id === value) : null;

    return (
      <>
        <Dropdown>
          <Dropdown.Toggle className="dropdown-outline" id={htmlId}>
            {selectedItem ? (
              selectedItem.isBestseller && !selectedItem.disabled ? (
                <span className="text-wrap">
                  {selectedItem.displayAs}
                  <span className="outline green-text">
                    {' '}
                    - {DEAL_CHOOSE_YOUR_OPTIONS_MODAL.bestseller}
                  </span>
                </span>
              ) : (
                <span className="text-wrap">{selectedItem.displayAs}</span>
              )
            ) : (
              `${DEAL_CHOOSE_YOUR_OPTIONS_MODAL.pleaseChoose} ${data.label}`
            )}
          </Dropdown.Toggle>

          <Dropdown.Menu className="full-width-list" id="menu-list">
            <Dropdown.Item
              className="full-width-list"
              onClick={() => action(0)}
            >
              {DEAL_CHOOSE_YOUR_OPTIONS_MODAL.pleaseChoose} {data.label}
            </Dropdown.Item>

            {data.items.map((item) => {
              if ((isLightPage() && !item.disabled) || !isLightPage())
                return (
                  <Dropdown.Item
                    className="dropdown-item"
                    disabled={item.disabled}
                    key={item.id}
                    onClick={() => action(item.id)}
                    value={item.id}
                  >
                    {item.isBestseller && !item.disabled ? (
                      <span className="green-text text-wrap">
                        {item.displayAs} -{' '}
                        {DEAL_CHOOSE_YOUR_OPTIONS_MODAL.bestseller}
                      </span>
                    ) : (
                      <span className="text-wrap">{item.displayAs}</span>
                    )}
                    {item.vipExclusive && (
                      <span className="green-text text-wrap">
                        {DEAL_CHOOSE_YOUR_OPTIONS_MODAL.vipExclusive}
                      </span>
                    )}
                  </Dropdown.Item>
                );

              return null;
            })}
          </Dropdown.Menu>
        </Dropdown>

        <style jsx>
          {`
            :global(.dropdown-outline) {
              width: 100%;
              background-color: transparent;
              color: ${theme.commoncolors.greycharcoal};
              border: 1px solid ${theme.commoncolors.inputiconcolor};
              font-size: 14px;
              min-width: 50px;
              min-height: 28px;
              border-radius: 5px;
              padding: 0px 10px 0px 8px;
              appearance: none;
              line-height: 1.2em;
              margin-bottom: 10px;
              text-align: left;
            }
            :global(.dropdown-outline:hover) {
              background-color: transparent;
              color: ${theme.commoncolors.greycharcoal};
              border: 1px solid ${theme.commoncolors.inputiconcolor};
            }
            :global(.dropdown-outline.dropdown-toggle) {
              background-color: transparent !important;
              color: ${theme.commoncolors.greycharcoal} !important;
              border: 1px solid ${theme.commoncolors.inputiconcolor} !important;
            }
            :global(.dropdown-outline::after) {
              float: right;
              margin-top: 6px;
            }
            :global(.full-width-list) {
              width: 100%;
              font-size: 13px;
            }
            :global(.dropdown-item) {
              display: flex;
              align-items: center;
              gap: 1rem;
            }
            .green-text {
              color: ${theme.commoncolors.assure};
            }
            .text-wrap {
              white-space: normal;
            }
            @media (max-width: ${theme.breakpoints.smUp}) {
              .outline.green-text {
                display: none;
              }
            }
          `}
        </style>
      </>
    );
  };

  const getProceedButtonText =
    payDeposit && isDepositDeal(deal)
      ? DEAL_CHOOSE_YOUR_OPTIONS_MODAL.reserveCheckout
      : DEAL_CHOOSE_YOUR_OPTIONS_MODAL.proceed;

  const ProceedButton = () => {
    return (
      <>
        {selectedOption < 0 && (
          <PrimaryButton
            aria-label={getProceedButtonText}
            className="size-larger full-width margin"
            onClick={showError}
          >
            {getProceedButtonText}
          </PrimaryButton>
        )}
      </>
    );
  };

  const ChooseYourOptionsInputs = () => {
    return (
      <div className="choose-your-options__inputs">
        {/* ROW SELECTION */}
        {selectBox({
          action: onRowChange,
          data: row,
          htmlId: 'rowId',
          value: rowSelection,
        })}

        {/* COLUMN SELECTION (HIDDEN IF ONLY 1 OPTION) */}
        {column.items.length > 1 &&
          selectBox({
            action: onSecondRowChange,
            data: column,
            htmlId: 'columnId',
            value: columnSelection,
          })}
        {column.items.length > 1
          ? (bestsellerProduct?.columnId === columnSelection ||
              bestsellerProduct?.rowId === rowSelection) && (
              <div className="deal-product-selector__footnote">
                {DEAL_CHOOSE_YOUR_OPTIONS_MODAL.bestsellerExplain}
              </div>
            )
          : bestsellerProduct?.rowId === rowSelection && (
              <div className="deal-product-selector__footnote">
                {DEAL_CHOOSE_YOUR_OPTIONS_MODAL.bestsellerExplain}
              </div>
            )}
        <div
          className={`deal-product-selector__empty-message ${
            showEmptyMessage ? 'visible ' : 'hidden'
          }`}
        >
          {DEAL_CHOOSE_YOUR_OPTIONS_MODAL.pleaseChooseOneOption}
        </div>
      </div>
    );
  };

  const PayAmount = () => {
    if (!isDepositDeal(deal)) return false;

    return columnSelection ? (
      <div className="cyo-deposit">
        <RadioButtonWithLabel
          checked={payDeposit}
          className={classNames('radio-button--boxed radio-button--switch', {
            'radio-button--green': payDeposit,
          })}
          label={
            <>
              <p className="label-text">
                {DEAL_CHOOSE_YOUR_OPTIONS_MODAL.payDeposit}
              </p>
              <p className="label-price">
                {formatCurrency(deal?.currency, deal?.depositPrice)}
              </p>
            </>
          }
          onChange={() => setPayDeposit(true)}
        />
        <RadioButtonWithLabel
          checked={!payDeposit}
          className={classNames('radio-button--boxed radio-button--switch', {
            'radio-button--green': !payDeposit,
          })}
          label={
            <>
              <p className="label-text">
                {DEAL_CHOOSE_YOUR_OPTIONS_MODAL.payFullDeposit}
              </p>
              <p className="label-price">
                {formatCurrency(deal?.currency, deal?.price)}
              </p>
            </>
          }
          onChange={() => setPayDeposit(false)}
        />
      </div>
    ) : null;
  };

  const SocialCues = () => {
    return (
      <div className="choose-your-options__cue">
        <div className="choose-your-options__discount">
          <MainDealDiscount
            deal={getDiscount(deal, rowSelection, columnSelection)}
          />
        </div>
        <SocialCueMainDeal
          className="main-deal-social-cue--assure main-deal-social-cue--no-margin"
          cue={
            deal.display?.bought && deal.totalBought === 0
              ? deal.newDealMessageTag
              : null
          }
        />
        <SocialCueMainDeal
          className="main-deal-social-cue--alert"
          cue={getModalSocialCue({
            deal,
            selected: selectedOption,
          })}
          isStarMarked
        />
        <SocialCueMainDeal
          className="main-deal-social-cue--assure"
          cue={getBackendSocialCueMessage(socialCuesData)}
        />
      </div>
    );
  };

  return (
    <>
      {isLayoutTravel && (
        <div className="cyo-block">
          <div className="cyo-header">
            {DEAL_CHOOSE_YOUR_OPTIONS_MODAL.chooseYourOptions}
          </div>
          <div className="cyo-body">
            <DepositMessage />
            <div className="cyo-options">{ChooseYourOptionsInputs()}</div>
            <PayAmount />
            <SocialCues />
            <div className="cyo-buttons">
              <ProceedButton />
              {selectedOption >= 0 && (
                <DealBuyButton
                  buttonRef={buttonRef}
                  className={
                    'size-larger deal-main-deal__buy full-width margin'
                  }
                  deal={deal}
                  handleBuyButtonClick={handleBuyButtonClick}
                  payDeposit={payDeposit}
                  position={selectedOption}
                  text={getProceedButtonText}
                  trackingEnabled={false}
                />
              )}
              {getGiftButton(selectedOption)}
            </div>
          </div>
        </div>
      )}

      {!isLayoutTravel && (
        <>
          <h2>{deal.headline}</h2>
          {!isDepositDeal(deal) && (
            <div className="choose-your-options__image-container">
              {imageVariant ? (
                <img
                  alt={deal.headline}
                  className="choose-your-options__image"
                  src={replaceImageServer({
                    dealId: deal.id,
                    image: imageVariant,
                  })}
                />
              ) : (
                <img
                  alt={deal.headline}
                  className="choose-your-options__image"
                  src={replaceImageServer({
                    dealId: deal.id,
                    image: deal?.images[0],
                  })}
                />
              )}
            </div>
          )}
          {secondCheckout && (
            <>
              <ListMainDeal
                className="highlights"
                list={deal.highlights}
                title={DEAL_CHOOSE_YOUR_OPTIONS_MODAL.productFeatures}
              />
              <MainDealFullDetails deal={deal} />
              <MainDealFinePrint
                collapsed={finePrintCollapsed}
                deal={deal}
                onToggle={() => setFinePrintCollapsed((state) => !state)}
              />
            </>
          )}
          <DepositMessage />
          {ChooseYourOptionsInputs()}
          {isVisible && <ChooseYourQuantity />}
          <SocialCues />
          <PayAmount />
          <ProceedButton />
          {selectedOption >= 0 && (
            <DealBuyButton
              className="size-larger deal-main-deal__buy full-width margin"
              deal={deal}
              handleBuyButtonClick={handleBuyButtonClick}
              payDeposit={payDeposit}
              position={selectedOption}
              secondCheckout={secondCheckout}
              text={getProceedButtonText}
              trackingEnabled={false}
            />
          )}
          {getGiftButton(selectedOption)}
        </>
      )}

      <style jsx>{`
        .cyo-header {
          background-color: ${theme.colors.primary};
          min-height: 50px;
          display: flex;
          flex-direction: column;
          justify-content: space-around;
          text-align: center;
          font-size: 20px;
          color: ${theme.colors.textonprimary};
          text-transform: capitalize;
        }

        .cyo-body {
          display: flex;
          flex-direction: column;
          border: 4px solid ${theme.colors.primary};
          border-top: none;
          padding: 20px;
        }
        .cyo-buttons {
          padding-top: 10px;
        }
        .cyo-block {
          margin-bottom: 20px;
        }
        :global(.cyo-deposit) {
          display: flex;
          padding: 5px 0px 10px 0px;
        }
        @media (max-width: ${theme.breakpoints.smDown}) {
          :global(.cyo-deposit) {
            padding-bottom: 10px;
          }
        }
        :global(.cyo-deposit label) {
          flex-grow: 1;
        }
        :global(.cyo-deposit label .label-text),
        :global(.cyo-deposit label .label-price) {
          margin: 0px;
        }
        :global(.cyo-deposit label:first-child) {
          margin-right: 10px;
        }

        :global(.choose-your-options__cue) {
          overflow: hidden;
        }
        @media (max-width: ${theme.breakpoints.smDown}) {
          :global(.choose-your-options__cue) {
            padding-top: 10px;
          }
        }
        :global(.choose-your-options__discount) {
          float: right;
          padding: 0 0 10px 10px;
        }
        .choose-your-options__image-container {
          padding: 20px 0px 20px 0px;
        }
        .choose-your-options__image {
          width: 100%;
        }
        .choose-your-options__inputs {
          padding: 0px 0px 12px 0px;
        }
        :global(.deal-product-selector__empty-message) {
          margin: 0 auto 20px;
          padding: 6px 12px;
          color: ${theme.commoncolors.alert};
          border: 4px solid ${theme.commoncolors.alert};
          border-radius: 4px;
          text-align: center;
        }
        :global(.deal-product-selector__empty-message.hidden) {
          display: none;
        }
        :global(.deal-product-selector__footnote) {
          margin: -8px 0 10px 0;
        }
        :global(.choose-your-options .modal-body) {
          padding: 0 20px 20px 20px;
        }
        :global(.choose-your-options__discount .deal-main-deal__discount) {
          background: ${theme.colors.primary};
        }
        :global(.choose-your-options .modal-content) {
          border: 1px solid ${theme.colors.primary};
        }
        :global(.size-larger) {
          position: ${layoutOptions.travel ? 'static' : 'sticky'};
          bottom: 0px;
          z-index: 1001;
        }
      `}</style>
    </>
  );
};

DealChooseYourOptionsModal.propTypes = {
  buttonRef: PropTypes.object,
  deal: PropTypes.object,
  finePrintCollapsed: PropTypes.bool,
  handleBuyButtonClick: PropTypes.func,
  initialColumnSel: PropTypes.number,
  isOptionSelected: PropTypes.func,
  isVisible: PropTypes.bool,
  layout: PropTypes.string,
  secondCheckout: PropTypes.bool,
  setFinePrintCollapsed: PropTypes.func,
};

export default DealChooseYourOptionsModal;
